import { styled } from '@mui/material/styles';
import {
  Box,
  Paper,
  Button,
  TextField,
  Typography,
  TableContainer,
  TablePagination,
  Chip,
  MenuItem,
  FormControl,
} from '@mui/material';
import { FORD_COLORS, FORD_TYPOGRAPHY, FORD_SPACING, FORD_RADIUS } from '../../theme/fordTheme.js';

// Common styling constants to reduce duplication
const COMMON_COLORS = {
  border: '#d1d5db',        // Light gray border for all fields
  accent: '#4f7cff',
  muted: '#9aa3c7',
  tableHeaderBg: 'rgba(30,45,90,0.8)',
  panelBg: 'rgba(30,45,90,0.18)',
};

const COMMON_STYLES = {
  borderRadius: '8px',
  panelBorderRadius: '12px',
  padding: '8px 10px',
  fieldPadding: '12px 14px',
};

// Base field styling to reduce duplication
const baseFieldStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    color: `${FORD_COLORS.textLight} !important`,
    borderRadius: COMMON_STYLES.borderRadius,
    '& fieldset': {
      borderColor: `${COMMON_COLORS.border} !important`,
    },
    '&:hover fieldset': {
      borderColor: `${COMMON_COLORS.accent} !important`,
    },
    '&.Mui-focused fieldset': {
      borderColor: `${COMMON_COLORS.accent} !important`,
    },
  },
  '& .MuiInputLabel-root': {
    color: `${FORD_COLORS.textLight} !important`,
    '&.Mui-focused': {
      color: `${COMMON_COLORS.accent} !important`,
    },
  },
};

// Base flex container styles
const baseFlexStyles = {
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  flexWrap: 'wrap',
};

// Base chip styles to reduce duplication
const baseChipStyles = {
  borderRadius: '999px',
  fontSize: '12px',
  height: '24px',
  fontWeight: 500,
  '& .MuiChip-label': {
    padding: '0 8px',
  },
};

// Base message styles
const baseMessageStyles = {
  textAlign: 'center',
  padding: '24px',
  fontSize: '14px',
};

// Main container - Ford theme
export const OutageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#e5e7eb',
  color: FORD_COLORS.textLight,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  // borderRadius: FORD_RADIUS.sm,
  padding: '28px',
  width: '100%',
  border: 'none',
  boxSizing: 'border-box',
  overflow: 'visible',
  // boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
}));

// Outer container with border - matching deviceTrouble page
export const OutageOuterContainer = styled(Box)(({ theme }) => ({
  // padding: '16px',
  minHeight: 'calc(100vh - 40px)',
  // borderRadius: FORD_RADIUS.sm,
  // border: '1px solid #233058', // Match Dashboard --border
  // borderRadius: '12px',
  backgroundColor:'#e5e7eb', // Match panel background
  boxSizing: 'border-box',
  // boxShadow: FORD_SHADOWS.md,
  width: '100%',
  maxWidth: '100vw',
  overflow: 'visible',
}));

// Page header - matching deviceTrouble page
export const OutagePageHeader = styled(Box)(({ theme }) => ({
  marginBottom: '22px',
  '& h2': {
    // margin: 0,
    // marginTop: '10px',
    // marginBottom: '8px',
    fontSize: FORD_TYPOGRAPHY.h4,
    fontWeight: FORD_TYPOGRAPHY.extraBold,
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    color: 'rgb(19, 34, 52)',
    // margin: `${FORD_SPACING.sm} 0`,
    letterSpacing: FORD_TYPOGRAPHY.letterSpacingWide
  },
  '& .subtitle': {
    color: 'rgb(94, 110, 126)',
    fontSize: FORD_TYPOGRAPHY.body2,
    marginTop: '4px',
    fontFamily: FORD_TYPOGRAPHY.newfont
  },
}));

// Panel container - matching deviceTrouble page
export const OutagePanel = styled(Paper)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford,
  // border: `1px solid ${COMMON_COLORS.border}`,
  borderRadius: COMMON_STYLES.panelBorderRadius,
  padding: '16px',
  color: '#e7ecff',
  // boxShadow: 'none',
  marginBottom: '16px',
  border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)'
}));

// Filters panel - special styling for filters form
export const OutageFiltersPanel = styled(OutagePanel)(({ theme }) => ({
  marginBottom: '22px',
}));

// Filters row container - main row with left and right sections
export const OutageFiltersRow = styled(Box)(({ theme }) => ({
  ...baseFlexStyles,
  justifyContent: 'space-between',
  width: '100%',
  '& > *': {
    minWidth: '150px',
  },
}));

// Left section for dropdowns
export const OutageFiltersLeft = styled(Box)(({ theme }) => ({
  ...baseFlexStyles,
  '& > *': {
    minWidth: '150px',
  },
}));

// Date filters container - right section
export const OutageDateFilters = styled(Box)(({ theme }) => ({
  ...baseFlexStyles,
  '& > *': {
    minWidth: '150px',
  },
}));

// Select field styling
export const OutageSelect = styled(FormControl)(({ theme }) => ({
  minWidth: '150px',
  ...baseFieldStyles,
  // font-size: 13.5px;
  '& .MuiSelect-select': {
    // color: `${FORD_COLORS.textLight} !important`,
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    paddingTop: '10px',
    paddingBottom: '8px',
    fontSize: '14px',
    fontFamily: FORD_TYPOGRAPHY.newfont
  },
  '& .MuiSelect-icon': {
    color: `${FORD_COLORS.textLight} !important`,
  },
  '& .MuiInputBase-root': {
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    color: `${FORD_COLORS.textLight} !important`
  },
}));

// MenuItem styling
export const OutageMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: `${FORD_COLORS.backgroundford} !important`,
  color: `${FORD_COLORS.textLight} !important`,
  '&:hover': {
    backgroundColor: `${FORD_COLORS.primaryLight} !important`,
  },
  '&.Mui-selected': {
    backgroundColor: `${FORD_COLORS.secondary} !important`,
    color: `${FORD_COLORS.textColorWhite} !important`,
    '&:hover': {
      backgroundColor: `${FORD_COLORS.primaryLight} !important`,
    },
  },
}));

// Date input field styling
export const OutageDateField = styled(TextField)(({ theme }) => ({
  minWidth: '200px',
  '& .MuiOutlinedInput-root': {
    backgroundColor: FORD_COLORS.backgroundford,
    color: FORD_COLORS.textMuted,
    borderRadius: COMMON_STYLES.borderRadius,
    '& fieldset': {
      borderColor: COMMON_COLORS.border,
    },
    '&:hover fieldset': {
      borderColor: COMMON_COLORS.accent,
    },
    '&.Mui-focused fieldset': {
      borderColor: COMMON_COLORS.accent,
    },
  },
  '& .MuiInputLabel-root': {
    color: FORD_COLORS.textMuted,
    '&.Mui-focused': {
      color: COMMON_COLORS.accent,
    },
  },
  '& .MuiOutlinedInput-input': {
    color: FORD_COLORS.textMuted,
    padding: COMMON_STYLES.fieldPadding,
    '&::-webkit-calendar-picker-indicator': {
      filter: 'invert(0)',
      cursor: 'pointer',
      opacity: 1,
      width: '20px',
      height: '20px',
      backgroundColor: 'transparent',
    },
    '&::-moz-calendar-picker-indicator': {
      filter: 'invert(0)',
      cursor: 'pointer',
      opacity: 1,
    },
  },
}));

// Primary button - matching deviceTrouble page
export const OutagePrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#066fef',
  color: '#ffffff',
  borderRadius: COMMON_STYLES.borderRadius,
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#0554c7',
  },
  '&:disabled': {
    backgroundColor: '#2a3a5c',
    color: COMMON_COLORS.muted,
    opacity: 0.7,
  },
}));

// Range display and download section
export const OutageRangeSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 0',
  borderTop: `1px solid ${COMMON_COLORS.border}`,
  marginTop: '16px',
  paddingTop: '16px',
}));

// Range text styling
export const OutageRangeText = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.textMuted,
  fontSize: '14px',
  marginTop: '6px',
  fontFamily: FORD_TYPOGRAPHY.newfont
}));

// KPIs container
export const OutageKpisContainer = styled(Box)(({ theme }) => ({
 marginBottom: '22px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
 borderRadius: COMMON_STYLES.panelBorderRadius,
  // border: none,
  padding: '12px 16px',
  background: FORD_COLORS.primary,
   '& .subtitle': {
    color: 'rgb(94, 110, 126)',
    fontSize: FORD_TYPOGRAPHY.body2,
    fontFamily: FORD_TYPOGRAPHY.newfont
  },
}));

// Individual KPI card
export const OutageKpiCard = styled(Box)(({ theme }) => ({
  backgroundColor: COMMON_COLORS.panelBg,
  border: `1px solid ${COMMON_COLORS.border}`,
  borderRadius: COMMON_STYLES.panelBorderRadius,
  padding: '16px',
  minWidth: '150px',
  textAlign: 'center',
  flex: '1',
}));

// KPI value styling
export const OutageKpiValue = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  color: COMMON_COLORS.accent,
  marginBottom: '4px',
}));

// KPI label styling
export const OutageKpiLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: COMMON_COLORS.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

// Table container - matching deviceTrouble page
export const OutageTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: 'transparent',
  marginTop: 8,
  border: `1px solid ${COMMON_COLORS.border}`,
  '& .MuiTable-root': {
    width: '100%',
    borderCollapse: 'collapse',
     fontFamily: FORD_TYPOGRAPHY.newfont,
    '& .MuiTableHead-root': {
      '& .MuiTableCell-root': {
        backgroundColor: 'rgb(10, 37, 64)', // Dark navy blue background
        color: '#ffffff', // White text
        fontWeight: 700,
        fontSize: '12px', // Smaller font size
fontFamily: FORD_TYPOGRAPHY.newfont, // Apply font to cells
        padding: '0px 16px', // Reduced padding
        textAlign: 'left',
        textTransform: 'uppercase', // Capital letters
        letterSpacing: '0.5px', // Better readability
        borderBottom: 'none',
        height: '42px'
      }
    },
    '& .MuiTableBody-root': {
     '& .MuiTableRow-root': {
    '&:hover': {
      backgroundColor: 'rgba(59, 130, 246, 0.05)', // Light blue hover effect
      cursor: 'pointer',
    },
  },
      '& .MuiTableCell-root': {
        color: FORD_COLORS.textMuted,
        padding: COMMON_STYLES.padding,
        textAlign: 'left',
        fontSize: '12px',
        fontFamily: FORD_TYPOGRAPHY.newfont, // Apply font to body cells
      }
    }
  }
}));

// Socket chip styling
export const OutageSocketChip = styled(Chip)(({ theme }) => ({
  ...baseChipStyles,
  backgroundColor: 'rgba(79, 124, 255, 0.2)',
  borderColor: COMMON_COLORS.accent,
  color: COMMON_COLORS.accent,
  border: `1px solid ${COMMON_COLORS.accent}`,
}));

// Code text for IPs and device IDs
export const OutageCodeText = styled('code')(({ theme }) => ({
  backgroundColor: 'rgba(12, 20, 46, 0.5)',
  color: COMMON_COLORS.muted,
  padding: '2px 6px',
  borderRadius: '4px',
  fontSize: '13px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
}));

// Loading message
export const OutageLoadingMessage = styled(Box)(({ theme }) => ({
  ...baseMessageStyles,
  color: FORD_COLORS.textMuted,
}));

// Error message
export const OutageErrorMessage = styled(Box)(({ theme }) => ({
  ...baseMessageStyles,
  color: '#ff6666',
  backgroundColor: 'rgba(255, 90, 90, 0.1)',
  border: '1px solid rgba(255, 90, 90, 0.3)',
  borderRadius: COMMON_STYLES.borderRadius,
}));

// No results message
export const OutageNoResults = styled(Box)(({ theme }) => ({
  ...baseMessageStyles,
  color: FORD_COLORS.textMuted,
}));

// Panel title text
export const OutagePanelTitle = styled('span')(({ theme }) => ({
  color: FORD_COLORS.textMuted, // Match Dashboard --muted
  fontSize: '13px',
  fontWeight: 700,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  letterSpacing: '0.5px',
  display: 'block',
  marginBottom: '16px',
}));

// Spacer for flex layouts
export const OutageSpacer = styled(Box)(({ theme }) => ({
  flex: 1,
}));

// Pagination container
export const OutagePaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '16px',
}));

// Styled TablePagination component
export const OutageTablePagination = styled(TablePagination)(({ theme }) => ({
  '& .MuiTablePagination-toolbar': {
    color: FORD_COLORS.textMuted,
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    color: FORD_COLORS.textMuted,
  },
  '& .MuiSelect-select': {
    color: FORD_COLORS.textMuted,
    backgroundColor: 'transparent',
  },
  '& .MuiIconButton-root': {
    color: FORD_COLORS.textMuted,
    '&:hover': {
      backgroundColor: `${FORD_COLORS.primaryLight}20`,
    },
    '&.Mui-disabled': {
      color: `${FORD_COLORS.textMuted}60`,
    },
  },
  '& .MuiSelect-icon': {
    color: FORD_COLORS.textMuted,
  },
}));

// Link button styling
export const OutageLinkButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(59, 130, 246, 0.1)', // Light blue background
  color: '#3b82f6', // Blue text
  borderRadius: '50%', // Circular shape
  width: '28px',
  height: '28px',
  minWidth: '28px',
  padding: 0,
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  '&:hover': {
    backgroundColor: 'rgba(59, 130, 246, 0.2)', // Slightly darker on hover
    textDecoration: 'none',
  },
}));

export const OutageFilterLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: FORD_TYPOGRAPHY.bold,
  color: 'rgb(94, 110, 126)',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontFamily: FORD_TYPOGRAPHY.newfont,
}));


export const OutageShellyHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  minHeight: '30px',
}));

// Left side text container
export const OutageShellyLeftText = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  '& .MuiTypography-root': {
    color: FORD_COLORS.textLight,
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
}));

// Right side KPIs container
export const OutageShellyRightKpis = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: FORD_SPACING.md,
  flexShrink: 0,
}));

// KPIs container
export const OutageKpisListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  marginBottom: '33px',
  flexWrap: 'wrap',
  position: 'relative',
  height: '115px',
  '&.loading-overlay': {
    opacity: 0.6,
    pointerEvents: 'none',
  },
}));